import { findFencedBlock } from './factory-questions';
import { indentFollowingLines, readFollowingLines } from './list-item-lines';
import { parseMentions } from './task-contexts';
import { cleanDisputeDetail, taskRef, type DisputeDetail, type ReviewFinding } from './factory-review';
import { findFactorySuggestions, parseSuggestionDecisions, roundForDecisions, suggestionsRunNumber, type SuggestionAction } from './factory-suggestions';

/**
 * SF-T-74: settling a disputed review in one click. When the builder disputes some of a review's
 * findings, the run's handoff note carries one fenced block listing them,
 *
 * ```factory-dispute
 * { "run": 172, "findings": [{ "title": "…", "where": "x.ts:12", "reason": "…", "suggestion": "retry", "task": "SF-T-138" }] }
 * ```
 *
 * which the task shows as a card in Decisions: for each finding, Leave it as the builder says, or Ask
 * the builder to fix it. The picks are saved as one message from the person (formatDisputeDecisions).
 * A finding whose `suggestion` names one of the run's suggestions is also settled by deciding that
 * suggestion: No thanks, Backlog and New branch leave it (the work goes in its own task, or nowhere);
 * Yes please fixes it on this branch; Ask decides nothing.
 *
 * A review the builder didn't report on (no factory-review block) carries `"unclear": true` and one
 * finding standing for the whole review: Accept the PR as it is, or Ask the builder to report its verdict.
 *
 * SF-T-300: each finding can also carry what its options mean (DisputeDetail: the reviewer's concern,
 * what leaving it and fixing it would do, the builder's recommendation) and a third option of the
 * builder's own (`other`), which goes back to the builder like a fix. Every choice takes a note; on
 * Leave it's for the record only.
 */

export const FACTORY_DISPUTE_LANG = 'factory-dispute';

const TITLE_MAX = 300;
const WHERE_MAX = 200;
const REASON_MAX = 1000;
const ID_MAX = 60;
const MAX_FINDINGS = 30;
/** Characters in a decision's note. */
export const DISPUTE_NOTE_MAX = 1000;

export interface DisputedFinding extends DisputeDetail {
  title: string;
  where?: string;
  /** Why the builder disputes it. */
  reason?: string;
  /** The id of the run's suggestion that is the same thing, when the builder named one. */
  suggestion?: string;
  /** SF-T-154: the sub-task it's about, on a PR several share ("SF-T-138"), when the builder said. */
  task?: string;
}

export interface FactoryDispute {
  /** The run whose review it is; null when the block names none. */
  run: number | null;
  findings: DisputedFinding[];
  /** The builder didn't say what it did about the findings: one finding stands for the whole review. */
  unclear?: boolean;
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** The block for a run's disputed findings, as the handoff note ends with it. */
export function factoryDisputeBlock(
  run: number,
  findings: Array<Pick<ReviewFinding, 'title' | 'where' | 'reason' | 'suggestion' | 'task' | 'concern' | 'leave' | 'fix' | 'other' | 'recommend'>>,
  options: { unclear?: boolean } = {}
): string {
  const dispute: FactoryDispute = {
    run,
    ...(options.unclear ? { unclear: true } : {}),
    findings: findings.slice(0, MAX_FINDINGS).map((f) => ({
      title: f.title,
      ...(f.where ? { where: f.where } : {}),
      ...(f.reason ? { reason: f.reason } : {}),
      ...(f.suggestion ? { suggestion: f.suggestion } : {}),
      ...(f.task ? { task: f.task } : {}),
      ...(f.concern ? { concern: f.concern } : {}),
      ...(f.leave ? { leave: f.leave } : {}),
      ...(f.fix ? { fix: f.fix } : {}),
      ...(f.other ? { other: f.other } : {}),
      ...(f.recommend ? { recommend: f.recommend } : {}),
    })),
  };
  return `\`\`\`${FACTORY_DISPUTE_LANG}\n${JSON.stringify(dispute, null, 2)}\n\`\`\``;
}

/** The block's JSON, checked and trimmed; null when it isn't usable (no findings). */
export function parseFactoryDisputeJson(raw: string): FactoryDispute | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = data as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.findings)) return null;
  const findings: DisputedFinding[] = [];
  for (const item of obj.findings.slice(0, MAX_FINDINGS)) {
    const f = (item ?? {}) as Record<string, unknown>;
    const title = text(f.title, TITLE_MAX);
    if (!title) continue;
    const where = text(f.where, WHERE_MAX);
    const reason = text(f.reason, REASON_MAX);
    const suggestion = text(f.suggestion, ID_MAX);
    const task = taskRef(f.task);
    findings.push({ title, ...(where ? { where } : {}), ...(reason ? { reason } : {}), ...(suggestion ? { suggestion } : {}), ...(task ? { task } : {}), ...cleanDisputeDetail(f) });
  }
  const run = typeof obj.run === 'number' && Number.isInteger(obj.run) && obj.run > 0 ? obj.run : null;
  if (!findings.length) return null;
  // An unclear review is one decision about the whole review.
  return obj.unclear === true ? { run, findings: findings.slice(0, 1), unclear: true } : { run, findings };
}

/** The first factory-dispute block in a message; null when there's none (or it's broken). */
export function findFactoryDispute(message: string): FactoryDispute | null {
  const block = findFencedBlock(message, FACTORY_DISPUTE_LANG);
  return block ? parseFactoryDisputeJson(block.body) : null;
}

// ---- the person's decisions ----

/** `other` is the builder's own third option (SF-T-300), only where the finding offers one. */
export const DISPUTE_CHOICES = ['leave', 'fix', 'other'] as const;
export type DisputeChoice = (typeof DISPUTE_CHOICES)[number];

export const DISPUTE_CHOICE_LABELS: Record<DisputeChoice, string> = {
  leave: 'Leave it as the builder says',
  fix: 'Ask the builder to fix it',
  other: 'Take the builder’s other option',
};

/** The same choices for an unclear review (the builder didn't report what it did); it has no other option. */
export const UNCLEAR_CHOICE_LABELS: Record<DisputeChoice, string> = {
  leave: 'Accept the PR as it is',
  fix: 'Ask the builder to report its verdict',
  other: 'Take the builder’s other option',
};

/** Whether a choice sends the finding back to the builder (a fix, or its other option). */
export const sendsBack = (choice: DisputeChoice) => choice !== 'leave';

/** Whether `choice` can be picked for this finding: `other` only when the builder offered one, and never on an unclear review. */
export const choiceOffered = (choice: DisputeChoice, finding: DisputedFinding, unclear = false) => choice !== 'other' || (!unclear && Boolean(finding.other));

/** The choices' labels for a dispute. */
export const disputeChoiceLabels = (dispute: Pick<FactoryDispute, 'unclear'>) => (dispute.unclear ? UNCLEAR_CHOICE_LABELS : DISPUTE_CHOICE_LABELS);

export const isDisputeChoice = (value: unknown): value is DisputeChoice => typeof value === 'string' && (DISPUTE_CHOICES as readonly string[]).includes(value);

/** One finding's decision; `number` is its place in the block (1-based). */
export interface DisputeDecision {
  number: number;
  title: string;
  choice: DisputeChoice;
  note: string;
  /** With `other`: the builder's option, as it labelled it. */
  option?: string;
}

const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();
const cleanTitle = (s: string) => oneLine(s).replace(/\*\*/g, '');
/** How far a note's further lines are indented under its decision (SF-T-265). */
const NOTE_INDENT = 3;

/** "**Disputed findings from R-172**", or "**Disputed findings**" when the run isn't known. */
export function disputeDecisionsHeader(runNumber: number | null): string {
  return runNumber === null ? '**Disputed findings**' : `**Disputed findings from R-${runNumber}**`;
}

/** The decisions as the chat message posted for the person: `1. **Title** → Leave it as the builder says: note`, then `footer`. */
export function formatDisputeDecisions(runNumber: number | null, decisions: DisputeDecision[], footer?: string, options: { unclear?: boolean } = {}): string {
  const labels = disputeChoiceLabels(options);
  const lines = [disputeDecisionsHeader(runNumber), ''];
  for (const d of decisions) {
    // SF-T-265: a note of several lines keeps them, indented under its decision.
    const note = indentFollowingLines(d.note, NOTE_INDENT);
    const option = d.choice === 'other' && d.option ? ` (“${oneLine(d.option).replace(/[“”]/g, '"')}”)` : '';
    lines.push(`${d.number}. **${cleanTitle(d.title)}** → ${labels[d.choice]}${option}${note ? `: ${note}` : ''}`);
  }
  if (footer) lines.push('', footer);
  return lines.join('\n');
}

const HEADER_RE = /^\*\*Disputed findings(?: from R-(\d+))?\*\*$/;
const LABEL_CHOICES = new Map(DISPUTE_CHOICES.flatMap((c) => [[DISPUTE_CHOICE_LABELS[c], c] as const, [UNCLEAR_CHOICE_LABELS[c], c] as const]));
const LINE_RE = new RegExp(`^(\\d+)\\.\\s+\\*\\*(.*?)\\*\\*\\s+→\\s+(${[...LABEL_CHOICES.keys()].join('|')})(?: \\(“([^”]*)”\\))?(?::\\s*(.*))?$`);

/** Reads back a message formatDisputeDecisions wrote; null when the message isn't one. */
export function parseDisputeDecisions(message: string): { runNumber: number | null; decisions: DisputeDecision[] } | null {
  const lines = message.replace(/\r\n?/g, '\n').trim().split('\n');
  const header = HEADER_RE.exec(lines[0]?.trim() ?? '');
  if (!header) return null;
  const decisions: DisputeDecision[] = [];
  for (let i = 1; i < lines.length; ) {
    const m = LINE_RE.exec(lines[i].trim());
    i++;
    if (!m) continue;
    const more = readFollowingLines(lines, i, NOTE_INDENT);
    i = more.next;
    const note = [(m[5] ?? '').trim(), more.text].filter(Boolean).join('\n');
    const choice = LABEL_CHOICES.get(m[3])!;
    decisions.push({ number: Number(m[1]), title: m[2], choice, note, ...(choice === 'other' && m[4] ? { option: m[4] } : {}) });
  }
  return decisions.length ? { runNumber: header[1] ? Number(header[1]) : null, decisions } : null;
}

/** What deciding a matching suggestion counts as: null for Ask (nothing decided yet). */
export function disputeChoiceForSuggestion(action: SuggestionAction): DisputeChoice | null {
  if (action === 'yes') return 'fix';
  if (action === 'ask') return null;
  return 'leave';
}

// ---- rounds ----

/** One finding's answer, and how it was given. */
export interface DisputeAnswer {
  choice: DisputeChoice;
  /** The dispute card, or deciding the matching suggestion (with what was picked). */
  via: 'card' | 'suggestion';
  action?: SuggestionAction;
  note: string;
  /** With `other`: the builder's option, as the decision named it. */
  option?: string;
  /** The person's message that gave it. */
  messageId: string;
}

export interface DisputeRound<M> {
  /** The factory's message with the factory-dispute block. */
  message: M;
  run: number | null;
  dispute: FactoryDispute;
  /** By finding number (1-based); the first answer on each counts. */
  answers: Map<number, DisputeAnswer>;
  /** Closed before every finding had an answer: a person's comment (the reply goes to the builder, as before), or a newer dispute. */
  closedBy: 'comment' | 'newer' | null;
  /** The people the run handed the task to (its waiting_added lines): a comment from one of them is their reply. */
  asked: Set<string>;
  /**
   * The factory's "R-12's review is settled" note came: the outcome (badge, waits) was applied, and the
   * round stays settled whatever happens to the messages that answered it (a decision undone).
   */
  settledNote: boolean;
}

/** Every finding has an answer. */
export const disputeAnswered = (round: DisputeRound<unknown>) => round.dispute.findings.every((_f, i) => round.answers.has(i + 1));
/** Settled: every finding answered, or the factory's settled note came (its outcome is applied). */
export const disputeComplete = (round: DisputeRound<unknown>) => round.settledNote || disputeAnswered(round);
/** Answered, but its outcome isn't applied yet (a failure part way): settling it again finishes it. */
export const disputeUnfinished = (round: DisputeRound<unknown>) => !round.closedBy && !round.settledNote && disputeAnswered(round);
/** Still waiting on someone to settle it. */
export const disputeOpen = (round: DisputeRound<unknown>) => !round.closedBy && !disputeComplete(round);
/** Findings still to answer. */
export const disputeRemaining = (round: DisputeRound<unknown>) => round.dispute.findings.length - round.answers.size;

const SETTLED_NOTE_RE = /^\*\*R-(\d+)'s review is settled\*\*/;

/** The factory's note once a round's outcome is applied: "**R-172's review is settled**: 1 left as the builder says." */
export function disputeSettledNote(run: number, what: string): string {
  return `**R-${run}'s review is settled**${what ? `: ${what}` : ''}.`;
}

/** What a settled round came to: how many findings were left as they are, and how many sent back (to fix, or for the builder's other option). */
export function disputeOutcome(round: DisputeRound<unknown>): DisputeOutcome {
  const answers = [...round.answers.values()];
  const other = answers.filter((a) => a.choice === 'other').length;
  return { left: answers.filter((a) => !sendsBack(a.choice)).length, fix: answers.filter((a) => sendsBack(a.choice)).length, ...(other ? { other } : {}) };
}

/** `fix` counts every finding sent back; `other`, how many of those went back for the builder's own option (SF-T-300). */
export interface DisputeOutcome {
  left: number;
  fix: number;
  other?: number;
}

/**
 * A task event as the rounds read it: chat messages (the factory's carry an actor label, "Factory ·
 * R-12", a person's don't), and the factory's waiting_added lines (who the run handed the task to).
 * Events without a kind are chat.
 */
export interface DisputeMessage {
  id: string;
  kind?: string;
  body: string | null;
  actor_label: string | null;
  actor_user_id?: string | null;
  payload?: unknown;
  created_at: string;
}

/**
 * A task's dispute rounds, oldest first, from its chat messages (oldest first). A person's dispute
 * decisions answer the newest open round of the run they name; their suggestion decisions answer the
 * findings that name those suggestions (in the same run's rounds, also when decided before the dispute
 * came). Any other message closes the open rounds it's the reply to, as it goes to the builder: one
 * that @mentions the AI builder, or one from a person the run handed the task to (its waiting_added
 * lines). Someone else's comment leaves them open.
 */
export function disputeRoundsOf<M extends DisputeMessage>(messages: M[]): DisputeRound<M>[] {
  type SuggestionRound = { id: string; run: number | null; ids: string[]; decided: Map<number, { action: SuggestionAction; note: string; messageId: string }> };
  const rounds: DisputeRound<M>[] = [];
  const suggestionRounds: SuggestionRound[] = [];

  const answerFromSuggestion = (round: DisputeRound<M>, ids: string[], number: number, d: { action: SuggestionAction; note: string; messageId: string }) => {
    const choice = disputeChoiceForSuggestion(d.action);
    const id = ids[number - 1];
    if (!choice || !id) return;
    round.dispute.findings.forEach((f, i) => {
      // SF-T-300: a finding with an option of the builder's own is settled on the card, where that option is offered.
      if (f.suggestion === id && !f.other && !round.answers.has(i + 1)) round.answers.set(i + 1, { choice, via: 'suggestion', action: d.action, note: d.note, messageId: d.messageId });
    });
  };

  for (const m of messages) {
    if (m.kind === 'waiting_added') {
      const userId = (m.payload as { waiting?: { target?: string; user_id?: string } } | null)?.waiting;
      if (m.actor_label && userId?.target === 'user' && userId.user_id) {
        for (const r of rounds) if (disputeOpen(r) && r.message.actor_label === m.actor_label) r.asked.add(userId.user_id);
      }
      continue;
    }
    if ((m.kind && m.kind !== 'chat') || !m.body) continue;
    if (m.actor_label) {
      const run = suggestionsRunNumber(m.actor_label);
      const suggestions = findFactorySuggestions(m.body);
      if (suggestions) suggestionRounds.push({ id: m.id, run, ids: suggestions.suggestions.suggestions.map((s) => s.id), decided: new Map() });
      const settled = SETTLED_NOTE_RE.exec(m.body);
      if (settled) {
        const ofRun = rounds.filter((r) => r.run === Number(settled[1]) && !r.closedBy);
        const round = ofRun[ofRun.length - 1];
        if (round) round.settledNote = true;
        continue;
      }
      const dispute = findFactoryDispute(m.body);
      if (!dispute) continue;
      for (const r of rounds) if (disputeOpen(r)) r.closedBy = 'newer';
      const round: DisputeRound<M> = { message: m, run: dispute.run ?? run, dispute, answers: new Map(), closedBy: null, asked: new Set(), settledNote: false };
      // Suggestions of the run decided before the dispute came settle their findings too.
      for (const s of suggestionRounds) if (s.run !== null && s.run === round.run) for (const [n, d] of s.decided) answerFromSuggestion(round, s.ids, n, d);
      rounds.push(round);
      continue;
    }
    const disputeDecisions = parseDisputeDecisions(m.body);
    if (disputeDecisions) {
      const open = rounds.filter((r) => disputeOpen(r) && (disputeDecisions.runNumber === null || r.run === disputeDecisions.runNumber));
      const round = open[open.length - 1];
      for (const d of disputeDecisions.decisions) {
        if (round && d.number >= 1 && d.number <= round.dispute.findings.length && !round.answers.has(d.number)) {
          round.answers.set(d.number, { choice: d.choice, via: 'card', note: d.note, ...(d.option ? { option: d.option } : {}), messageId: m.id });
        }
      }
      continue;
    }
    const suggestionDecisions = parseSuggestionDecisions(m.body);
    if (suggestionDecisions) {
      const s = roundForDecisions(suggestionRounds, suggestionDecisions, (r) => r.run, (r) => r.id);
      if (!s) continue;
      for (const d of suggestionDecisions.decisions) {
        if (s.decided.has(d.number)) continue;
        const decided = { action: d.action, note: d.note, messageId: m.id };
        s.decided.set(d.number, decided);
        if (s.run === null) continue;
        for (const r of rounds) if (r.run === s.run && disputeOpen(r)) answerFromSuggestion(r, s.ids, d.number, decided);
      }
      continue;
    }
    const toBuilder = parseMentions(m.body).aiBuilder;
    for (const r of rounds) if (disputeOpen(r) && (toBuilder || (m.actor_user_id && r.asked.has(m.actor_user_id)))) r.closedBy = 'comment';
  }
  return rounds;
}
